// src/stores/useManagedAccountStore.js  (PATCH v2)

import { defineStore } from "pinia";
import * as managedAccountService from "@/services/managedAccountService";

export const useManagedAccountStore = defineStore("managedAccount", {
  state: () => ({
    // ── Lista ativa (GET /managed-accounts) ──────────────────────────────────
    managedAccounts: [],

    // ── Paginação (GET /managed-accounts/relationships/paged) ────────────────
    pagedRelationships: [],
    totalCount: 0,
    totalActiveCount: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 10,

    // ── Busca persistida (espelho de associationSearchTerm no UserGroupStore) ─
    relationshipSearchTerm: "",

    // ── Seleção para ações em lote ───────────────────────────────────────────
    selectedRelationshipIds: [],

    // ── Vínculo selecionado para edição / visualização / exclusão ────────────
    // (espelho de selectedAssociation no UserGroupStore)
    selectedRelationship: null,

    // ── Flag de reset visual de paginação (espelho de shouldForcePageOne) ────
    shouldForcePageOne: false,

    // ── Consumo agregado ─────────────────────────────────────────────────────
    aggregatedUsage: null,

    // Permissões efetivas do usuário logado quando este é membro gerenciado.
    // null = sem restrição (titular ou usuário comum).
    currentMemberPermissions: null,

    // Usuários ligados ao logado por vínculo ativo (titulares dele + membros
    // que ele gerencia). Carregado sob demanda por ensureRelatedUsers().
    relatedUsers: [],
    relatedUsersLoaded: false,
    relatedUsersRequest: null,

    // Ids do círculo do contexto de login ativo (titular + membros ativos dele).
    // Usado para saber em quais atividades o membro pode anotar quando a opção
    // "somente conteúdo do titular" está ligada. Carregado por ensureCircle().
    circleUserIds: [],
    circleLoaded: false,
    circleRequest: null,

    // ── Estado geral ─────────────────────────────────────────────────────────
    error: null,
    isLoading: false,
  }),

  getters: {
    // Número de contas ativas
    activeCount: (state) => state.managedAccounts.length,

    // Verifica se todos os IDs de uma página estão selecionados
    isAllSelectedOnPage: (state) => (pageIds) => {
      if (!pageIds || pageIds.length === 0) return false;
      return pageIds.every((id) => state.selectedRelationshipIds.includes(id));
    },

    // Verifica se há algum item selecionado (habilita ações em lote)
    canBatchActions: (state) => state.selectedRelationshipIds.length > 0,

    // IDs dos usuários ligados ao logado (titulares dele + membros que gerencia)
    relatedUserIds: (state) =>
      state.relatedUsers
        .map((u) => u.userId ?? u.UserId)
        .filter((id) => id != null),
  },

  actions: {
    // ─── Seleção ─────────────────────────────────────────────────────────────

    /** Alterna a seleção de um vínculo pelo ID. */
    toggleRelationshipSelection(id) {
      const idx = this.selectedRelationshipIds.indexOf(id);
      if (idx > -1) this.selectedRelationshipIds.splice(idx, 1);
      else this.selectedRelationshipIds.push(id);
    },

    /** Seleciona todos os IDs fornecidos. */
    selectAllRelationships(ids) {
      this.selectedRelationshipIds = [...ids];
    },

    /** Limpa toda a seleção. */
    clearSelection() {
      this.selectedRelationshipIds = [];
    },

    /** Define o vínculo atualmente selecionado (para edit/view/delete). */
    setSelectedRelationship(relationship) {
      this.selectedRelationship = relationship ? { ...relationship } : null;
    },

    // ─── Busca persistida ────────────────────────────────────────────────────

    /** Atualiza o termo de busca e recarrega da página 1. */
    setSearchTerm(term) {
      this.relationshipSearchTerm = term;
      this.currentPage = 1;
      this.fetchPaged(1, this.pageSize, term);
    },

    // ─── Listagem ativa ──────────────────────────────────────────────────────

    /**
     * Carrega todas as contas gerenciadas ativas do titular.
     * Mantém alias fetchManagedAccounts para retro-compatibilidade.
     */
    async fetchManagedAccounts() {
      this.isLoading = true;
      try {
        const response = await managedAccountService.fetchAll();
        this.managedAccounts = response.data;
        this.error = null;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        this.error = message;
        console.error("❌ Erro ao buscar contas gerenciadas:", message);
        throw new Error(message);
      } finally {
        this.isLoading = false;
      }
    },

    // ─── Listagem paginada ───────────────────────────────────────────────────

    /**
     * Carrega vínculos paginados (todos os status).
     * @param {number} pageNumber
     * @param {number} pageSize
     * @param {string} search
     */
    async fetchPaged(pageNumber = 1, pageSize = 10, search = "") {
      this.isLoading = true;
      try {
        const response = await managedAccountService.fetchPaged(
          pageNumber,
          pageSize,
          search,
        );
        this.pagedRelationships = response.data.data;
        this.totalCount = response.data.totalCount;
        this.totalActiveCount = response.data.totalActiveCount ?? 0;
        this.totalPages = response.data.totalPages;
        this.currentPage = pageNumber;
        this.relationshipSearchTerm = search;
        this.error = null;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        this.error = message;
        this.totalActiveCount = 0;
        console.error("❌ Erro ao buscar vínculos paginados:", message);
        throw new Error(message);
      } finally {
        this.isLoading = false;
      }
    },

    // ─── Criar novo usuário + vínculo ────────────────────────────────────────

    /**
     * Cria um novo usuário e o vincula ao titular autenticado.
     * @param {object} data - ManagedAccountCreateDto
     */
    async createManagedAccount(data) {
      try {
        const response = await managedAccountService.create(data);
        await this.fetchManagedAccounts();
        return response.data;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao criar conta gerenciada:", message);
        throw new Error(message);
      }
    },

    // ─── Vincular usuário existente ──────────────────────────────────────────

    /**
     * Convida um usuário já existente para o círculo. Devolve o convite: o
     * vínculo só aparece na lista depois que a pessoa aceita.
     * @param {object} data - ManagedAccountLinkDto
     */
    async linkExistingUser(data) {
      try {
        const response = await managedAccountService.linkExisting(data);
        await this.fetchManagedAccounts();
        return response.data;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao vincular usuário existente:", message);
        throw new Error(message);
      }
    },

    // ─── Bulk link ───────────────────────────────────────────────────────────

    /**
     * Convida múltiplos usuários existentes em lote. Devolve os convites.
     * @param {object} data - ManagedAccountBulkLinkDto
     */
    async bulkLinkExistingUsers(data) {
      try {
        const response = await managedAccountService.bulkLinkByPairs(data);
        await this.fetchManagedAccounts();
        return response.data;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao vincular usuários em lote:", message);
        throw new Error(message);
      }
    },

    // ─── Revogar singular ────────────────────────────────────────────────────

    /**
     * Revoga (soft-delete) um vínculo singular e recarrega as listas.
     * @param {number} id - ID do relacionamento
     */
    async revokeRelationship(id) {
      try {
        await managedAccountService.revokeById(id);
        await this.fetchPaged(
          this.currentPage,
          this.pageSize,
          this.relationshipSearchTerm,
        );
        await this.fetchManagedAccounts();
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao revogar vínculo:", message);
        throw new Error(message);
      }
    },

    // ─── Bulk revoke ─────────────────────────────────────────────────────────

    /**
     * Revoga em lote todos os vínculos atualmente selecionados.
     * Limpa a seleção e recarrega as listas após o sucesso.
     */
    async bulkRevoke() {
      if (!this.selectedRelationshipIds.length) return;
      try {
        await managedAccountService.bulkRevokeByIds({
          relationshipIds: this.selectedRelationshipIds,
        });
        this.clearSelection();
        await this.fetchPaged(
          this.currentPage,
          this.pageSize,
          this.relationshipSearchTerm,
        );
        await this.fetchManagedAccounts();
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao revogar vínculos em lote:", message);
        throw new Error(message);
      }
    },

    // ─── Alternar status ─────────────────────────────────────────────────────

    /**
     * Alterna o status de um vínculo (Active / Inactive / Revoked).
     * Atualiza o item localmente para reatividade imediata e recarrega a lista ativa.
     * @param {number} id     - ID do relacionamento
     * @param {string} status - 'Active' | 'Inactive' | 'Revoked'
     */
    async updateStatus(id, status) {
      try {
        const response = await managedAccountService.updateStatus(id, {
          status,
        });
        // Atualiza o item na lista paginada localmente para reatividade imediata
        const idx = this.pagedRelationships.findIndex(
          (r) => r.managedAccountRelationshipId === id,
        );
        if (idx !== -1) {
          this.pagedRelationships[idx] = response.data;
        }
        await this.fetchManagedAccounts();
        return response.data;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao alterar status do vínculo:", message);
        throw new Error(message);
      }
    },

    // ─── Consumo agregado ────────────────────────────────────────────────────

    /** Carrega o consumo agregado do titular + todos os gerenciados ativos. */
    async fetchAggregatedUsage() {
      try {
        const response = await managedAccountService.fetchAggregatedUsage();
        this.aggregatedUsage = response.data;
        return response.data;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao buscar consumo agregado:", message);
        throw new Error(message);
      }
    },

    // ─── Permissões granulares ───────────────────────────────────────────────

    /**
     * Busca e armazena as permissões efetivas do usuário logado.
     * Se for membro gerenciado, retorna o objeto de permissões.
     * Se for titular ou usuário comum, retorna null (sem restrição).
     * Deve ser chamado após _handleLoginSuccess e tryAutoLogin.
     */
    async fetchMyPermissions() {
      try {
        const response = await managedAccountService.resolveMyPermissions();
        // backend retorna null ou { texts, notes, activities }
        this.currentMemberPermissions = response.data ?? null;
        return this.currentMemberPermissions;
      } catch (error) {
        // Falha silenciosa: sem permissões = sem restrição (comportamento seguro)
        console.warn(
          "[ManagedAccountStore] Falha ao resolver permissões:",
          error?.message,
        );
        this.currentMemberPermissions = null;
        return null;
      }
    },

    /**
     * Atualiza as permissões granulares de um vínculo Membro-Titular.
     * @param {number} relationshipId
     * @param {{ texts, notes, activities }} permissions
     */
    async updatePermissions(relationshipId, permissions) {
      try {
        const response = await managedAccountService.updatePermissions(
          relationshipId,
          { permissions },
        );
        // Atualiza o item na lista paginada se estiver carregado
        const idx = this.pagedRelationships.findIndex(
          (r) => r.managedAccountRelationshipId === relationshipId,
        );
        if (idx !== -1) {
          this.pagedRelationships[idx] = response.data;
        }
        return response.data;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao atualizar permissões do vínculo:", message);
        throw new Error(message);
      }
    },

    async updateRelationship(relationshipId, data) {
      try {
        const response = await managedAccountService.updateRelationship(
          relationshipId,
          data,
        );

        const idx = this.pagedRelationships.findIndex(
          (r) => r.managedAccountRelationshipId === relationshipId,
        );

        if (idx !== -1) {
          this.pagedRelationships[idx] = response.data;
        }

        return response.data;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao atualizar vínculo:", message);
        throw new Error(message);
      }
    },

    /**
     * Resolve e armazena as permissões efetivas do usuário logado.
     * Chamado no login e no tryAutoLogin para membros.
     * Se o usuário não é membro, currentMemberPermissions fica null.
     * @param {{ texts, notes, activities }|null} permissions
     */
    setCurrentMemberPermissions(permissions) {
      this.currentMemberPermissions = permissions ?? null;
    },

    // ─── Círculo de vínculos (titulares + membros) ───────────────────────────

    /**
     * Busca os usuários ligados ao logado por vínculo ativo.
     * Falha silenciosa: lista vazia = ninguém vinculado (comportamento seguro,
     * equivale ao que já acontecia antes deste recurso existir).
     */
    async fetchRelatedUsers() {
      try {
        const response = await managedAccountService.fetchMyRelatedUsers();
        this.relatedUsers = response.data ?? [];
      } catch (error) {
        console.warn(
          "[ManagedAccountStore] Falha ao buscar usuários vinculados:",
          error?.message,
        );
        this.relatedUsers = [];
      } finally {
        this.relatedUsersLoaded = true;
      }
      return this.relatedUsers;
    },

    /**
     * Garante que relatedUsers esteja carregado, sem repetir a requisição
     * quando vários formulários de permissão são abertos na mesma sessão.
     */
    async ensureRelatedUsers() {
      if (this.relatedUsersLoaded) return this.relatedUsers;
      if (!this.relatedUsersRequest) {
        this.relatedUsersRequest = this.fetchRelatedUsers().finally(() => {
          this.relatedUsersRequest = null;
        });
      }
      return this.relatedUsersRequest;
    },

    /**
     * Busca os ids do círculo do contexto ativo (titular + membros dele).
     */
    async fetchMyCircle() {
      try {
        const response = await managedAccountService.fetchMyCircle();
        this.circleUserIds = (response.data ?? []).map((id) => Number(id));
        this.circleLoaded = true;
        return this.circleUserIds;
      } catch (error) {
        console.warn(
          "[useManagedAccountStore] Falha ao carregar o círculo:",
          error.response?.data?.message || error.message
        );
        this.circleUserIds = [];
        this.circleLoaded = true;
        return this.circleUserIds;
      }
    },

    /**
     * Garante o círculo carregado sem repetir a requisição.
     */
    async ensureCircle() {
      if (this.circleLoaded) return this.circleUserIds;
      if (!this.circleRequest) {
        this.circleRequest = this.fetchMyCircle().finally(() => {
          this.circleRequest = null;
        });
      }
      return this.circleRequest;
    },

    /** Limpa o cache do círculo de vínculos (logout / troca de usuário). */
    clearRelatedUsers() {
      this.relatedUsers = [];
      this.relatedUsersLoaded = false;
      this.relatedUsersRequest = null;
    },
  },
});
